/**
 * @page PreiskalkulatorConfig
 * @route /preiskalkulator/config
 * @description Admin-Konfiguration für den Preiskalkulator.
 *   6 Tabs: Handling-Gebühren, Verpackung, Porto, Zusatzleistungen, Mengenstaffeln, Produktkategorien.
 *   CRUD / Inline-Edit für alle Kalkulator-Stammdaten.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SlidersHorizontal, Plus, Pencil, Trash2, Save, X, Check, Package, Truck, Tag, Layers, Grid3X3, Star } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// ── Helpers ──────────────────────────────────────────────────────────
const apiFetch = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: 'include', ...opts }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });

const apiPost = (path, body) =>
  apiFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const apiPut = (path, body) =>
  apiFetch(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const apiDel = (path) =>
  apiFetch(path, { method: 'DELETE' });

const fmtPrice = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '0,00' : n.toFixed(2).replace('.', ',');
};

const parseLocaleNum = (s) => {
  if (typeof s === 'number') return s;
  return parseFloat(String(s).replace(',', '.')) || 0;
};

// ── Tab definitions ──
const TABS = [
  { key: 'handling', labelKey: 'calcConfig.tabHandling', Icon: Grid3X3 },
  { key: 'packaging', labelKey: 'calcConfig.tabPackaging', Icon: Package },
  { key: 'porto', labelKey: 'calcConfig.tabPorto', Icon: Truck },
  { key: 'extras', labelKey: 'calcConfig.tabExtras', Icon: Star },
  { key: 'tiers', labelKey: 'calcConfig.tabTiers', Icon: Layers },
  { key: 'categories', labelKey: 'calcConfig.tabCategories', Icon: Tag },
];

// ═══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function PreiskalkulatorConfig() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('handling');

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--primary-light,#EBF4FD)] flex items-center justify-center">
          <SlidersHorizontal size={18} className="text-[var(--primary,#338edc)]" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-[var(--text-primary)]">{t('calcConfig.title')}</h1>
          <p className="text-xs text-[var(--text-secondary)]">{t('calcConfig.subtitle')}</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-[2px]">
        {TABS.map(({ key, labelKey, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all flex items-center gap-1.5
              ${tab === key
                ? 'bg-[var(--bg-card,#fff)] text-[var(--text-primary)] shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
          >
            <Icon size={13} />
            {t(labelKey)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'handling' && <HandlingTab t={t} />}
      {tab === 'packaging' && <PackagingTab t={t} />}
      {tab === 'porto' && <PortoTab t={t} />}
      {tab === 'extras' && <ExtrasTab t={t} />}
      {tab === 'tiers' && <TiersTab t={t} />}
      {tab === 'categories' && <CategoriesTab t={t} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  1) HANDLING TAB — Matrix inline-edit
// ═══════════════════════════════════════════════════════════════════════
function HandlingTab({ t }) {
  const [fees, setFees] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [f, ti] = await Promise.all([
      apiFetch('/api/preiskalkulator/handling-fees'),
      apiFetch('/api/preiskalkulator/volume-tiers'),
    ]);
    setFees(f);
    setTiers(ti);
    setDirty({});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group by position + weight range
  const positions = [...new Set(fees.map(f => f.position))].sort((a, b) => a - b);
  const weightClasses = [...new Map(fees.map(f => [`${f.weight_min}-${f.weight_max}`, { weight_min: f.weight_min, weight_max: f.weight_max }])).values()]
    .sort((a, b) => a.weight_max - b.weight_max);

  const getCell = (pos, wmin, wmax, tierId) =>
    fees.find(f => f.position === pos && f.weight_min === wmin && f.weight_max === wmax && f.volume_tier_id === tierId);

  const handleChange = (id, val) => {
    setDirty(prev => ({ ...prev, [id]: val }));
  };

  const saveAll = async () => {
    setSaving(true);
    for (const [id, val] of Object.entries(dirty)) {
      await apiPut(`/api/preiskalkulator/handling-fees/${id}`, { price: parseLocaleNum(val) });
    }
    await load();
    setSaving(false);
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcConfig.handlingDesc')}</p>
        {Object.keys(dirty).length > 0 && (
          <button onClick={saveAll} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
            <Save size={13} /> {saving ? t('common.saving') || 'Saving...' : t('calcConfig.saveAll')}
            <span className="ml-1 bg-white/20 rounded px-1.5">{Object.keys(dirty).length}</span>
          </button>
        )}
      </div>

      {positions.map(pos => (
        <div key={pos} className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-[var(--bg-inset)] border-b border-[var(--border-default,#E5E7EB)]">
            <span className="text-sm font-semibold text-[var(--text-primary)]">
              {t('calcConfig.position')} {pos}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--border-default,#E5E7EB)]">
                  <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium w-28">{t('calcConfig.weightClass')}</th>
                  {tiers.map(ti => (
                    <th key={ti.id} className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{ti.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weightClasses.map(wc => (
                  <tr key={`${wc.weight_min}-${wc.weight_max}`} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                    <td className="px-3 py-1.5 text-[var(--text-primary)] font-medium whitespace-nowrap">
                      {wc.weight_min > 0 ? `${fmtPrice(wc.weight_min)} – ` : '≤ '}{fmtPrice(wc.weight_max)} kg
                    </td>
                    {tiers.map(ti => {
                      const cell = getCell(pos, wc.weight_min, wc.weight_max, ti.id);
                      if (!cell) return <td key={ti.id} className="px-3 py-1.5 text-center text-[var(--text-muted)]">—</td>;
                      const val = dirty[cell.id] !== undefined ? dirty[cell.id] : fmtPrice(cell.price);
                      return (
                        <td key={ti.id} className="px-2 py-1">
                          <input
                            type="text"
                            value={val}
                            onChange={e => handleChange(cell.id, e.target.value)}
                            className="w-full text-center text-xs px-1.5 py-1 rounded border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  2) PACKAGING TAB — CRUD table + modal
// ═══════════════════════════════════════════════════════════════════════
function PackagingTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | item

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await apiFetch('/api/preiskalkulator/packaging-materials'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcConfig.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/packaging-materials/${id}`);
    load();
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcConfig.packagingDesc')}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={13} /> {t('calcConfig.addPackaging')}
        </button>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colName')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colCode')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colCategory')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colDimensions')}</th>
              <th className="text-right px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colPrice')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colColor')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colActive')}</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{it.name}</td>
                <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{it.code}</td>
                <td className="px-3 py-2 text-[var(--text-secondary)]">{it.category}</td>
                <td className="px-3 py-2 text-center text-[var(--text-secondary)]">
                  {it.dim_length && it.dim_width ? `${it.dim_length}×${it.dim_width}${it.dim_height ? `×${it.dim_height}` : ''} mm` : '—'}
                </td>
                <td className="px-3 py-2 text-right text-[var(--text-primary)] font-medium">{fmtPrice(it.price)} €</td>
                <td className="px-3 py-2 text-center">
                  {it.color && <span className="inline-block w-4 h-4 rounded-full border border-[var(--border-default,#E5E7EB)]" style={{ background: it.color }} />}
                </td>
                <td className="px-3 py-2 text-center">
                  {it.active ? <Check size={14} className="inline text-green-500" /> : <X size={14} className="inline text-[var(--text-muted)]" />}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]" title={t('common.edit') || 'Edit'}>
                      <Pencil size={13} className="text-[var(--text-secondary)]" />
                    </button>
                    <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]" title={t('common.delete') || 'Delete'}>
                      <Trash2 size={13} className="text-[var(--status-error)]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('calcConfig.noData')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <PackagingModal
          t={t}
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function PackagingModal({ t, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    code: item?.code || '',
    category: item?.category || 'karton',
    dim_length: item?.dim_length || '',
    dim_width: item?.dim_width || '',
    dim_height: item?.dim_height || '',
    price: item ? fmtPrice(item.price) : '0,00',
    color: item?.color || '#338edc',
    sort_order: item?.sort_order ?? 0,
    active: item?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      dim_length: form.dim_length ? parseInt(form.dim_length) : null,
      dim_width: form.dim_width ? parseInt(form.dim_width) : null,
      dim_height: form.dim_height ? parseInt(form.dim_height) : null,
      price: parseLocaleNum(form.price),
      sort_order: parseInt(form.sort_order) || 0,
    };
    if (item) {
      await apiPut(`/api/preiskalkulator/packaging-materials/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/packaging-materials', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-lg p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcConfig.editPackaging') : t('calcConfig.addPackaging')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colName')} *</span>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colCode')} *</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colCategory')}</span>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none">
                <option value="karton">Karton</option>
                <option value="versandtasche">Versandtasche</option>
                <option value="sonstiges">Sonstiges</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colPrice')} (€)</span>
              <input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.dimLength')}</span>
              <input type="number" value={form.dim_length} onChange={e => setForm(f => ({ ...f, dim_length: e.target.value }))} placeholder="mm"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.dimWidth')}</span>
              <input type="number" value={form.dim_width} onChange={e => setForm(f => ({ ...f, dim_width: e.target.value }))} placeholder="mm"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.dimHeight')}</span>
              <input type="number" value={form.dim_height} onChange={e => setForm(f => ({ ...f, dim_height: e.target.value }))} placeholder="mm"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colColor')}</span>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full h-8 rounded-lg border border-[var(--border-input)] cursor-pointer" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.sortOrder')}</span>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
              <span className="text-xs text-[var(--text-secondary)]">{t('calcConfig.colActive')}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  3) PORTO TAB — Route filter + weight × tier matrix
// ═══════════════════════════════════════════════════════════════════════
function PortoTab({ t }) {
  const [rates, setRates] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  // Filter state
  const [selCarrier, setSelCarrier] = useState('');
  const [selWarehouse, setSelWarehouse] = useState('');
  const [selCountry, setSelCountry] = useState('');

  // Init modal
  const [showInit, setShowInit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, ca, wa, co, ti] = await Promise.all([
      apiFetch('/api/preiskalkulator/porto-rates'),
      apiFetch('/api/preiskalkulator/carriers'),
      apiFetch('/api/preiskalkulator/warehouses'),
      apiFetch('/api/preiskalkulator/countries'),
      apiFetch('/api/preiskalkulator/volume-tiers'),
    ]);
    setRates(r);
    setCarriers(ca);
    setWarehouses(wa);
    setCountries(co);
    setTiers(ti);
    setDirty({});
    setLoading(false);

    // Auto-select first route
    if (r.length && !selCarrier) {
      setSelCarrier(String(r[0].carrier_id));
      setSelWarehouse(r[0].warehouse_code);
      setSelCountry(r[0].country_code);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter rates for selected route
  const filtered = rates.filter(r =>
    (!selCarrier || String(r.carrier_id) === selCarrier) &&
    (!selWarehouse || r.warehouse_code === selWarehouse) &&
    (!selCountry || r.country_code === selCountry)
  );

  // Group by weight class
  const weightClasses = [...new Map(filtered.map(r => [
    `${r.weight_min}-${r.weight_max}`,
    { weight_min: r.weight_min, weight_max: r.weight_max }
  ])).values()].sort((a, b) => a.weight_max - b.weight_max);

  const getCell = (wmin, wmax, tierId) =>
    filtered.find(r => r.weight_min === wmin && r.weight_max === wmax && r.volume_tier_id === tierId);

  const handleChange = (id, val) => setDirty(prev => ({ ...prev, [id]: val }));

  const saveAll = async () => {
    setSaving(true);
    for (const [id, val] of Object.entries(dirty)) {
      await apiPut(`/api/preiskalkulator/porto-rates/${id}`, { price: parseLocaleNum(val) });
    }
    await load();
    setSaving(false);
  };

  // Available routes for dropdowns
  const routeCarriers = [...new Map(rates.map(r => [String(r.carrier_id), { id: r.carrier_id, name: r.carrier_name }])).values()];
  const routeWarehouses = [...new Set(rates.filter(r => !selCarrier || String(r.carrier_id) === selCarrier).map(r => r.warehouse_code))];
  const routeCountries = [...new Set(rates.filter(r =>
    (!selCarrier || String(r.carrier_id) === selCarrier) &&
    (!selWarehouse || r.warehouse_code === selWarehouse)
  ).map(r => r.country_code))];

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcConfig.portoDesc')}</p>
        <div className="flex gap-2">
          <button onClick={() => setShowInit(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--border-default,#E5E7EB)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
            <Plus size={13} /> {t('calcConfig.initRoute')}
          </button>
          {Object.keys(dirty).length > 0 && (
            <button onClick={saveAll} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              <Save size={13} /> {saving ? t('common.saving') || 'Saving...' : t('calcConfig.saveAll')}
              <span className="ml-1 bg-white/20 rounded px-1.5">{Object.keys(dirty).length}</span>
            </button>
          )}
        </div>
      </div>

      {/* Route filter */}
      <div className="flex gap-3">
        <select value={selCarrier} onChange={e => { setSelCarrier(e.target.value); setDirty({}); }}
          className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
          <option value="">{t('calcConfig.allCarriers')}</option>
          {routeCarriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={selWarehouse} onChange={e => { setSelWarehouse(e.target.value); setDirty({}); }}
          className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
          <option value="">{t('calcConfig.allWarehouses')}</option>
          {routeWarehouses.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
        <select value={selCountry} onChange={e => { setSelCountry(e.target.value); setDirty({}); }}
          className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
          <option value="">{t('calcConfig.allCountries')}</option>
          {routeCountries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Matrix */}
      {weightClasses.length > 0 ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
                  <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium w-28">{t('calcConfig.weightClass')}</th>
                  {tiers.map(ti => (
                    <th key={ti.id} className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{ti.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weightClasses.map(wc => (
                  <tr key={`${wc.weight_min}-${wc.weight_max}`} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                    <td className="px-3 py-1.5 text-[var(--text-primary)] font-medium whitespace-nowrap">
                      ≤ {fmtPrice(wc.weight_max)} kg
                    </td>
                    {tiers.map(ti => {
                      const cell = getCell(wc.weight_min, wc.weight_max, ti.id);
                      if (!cell) return <td key={ti.id} className="px-3 py-1.5 text-center text-[var(--text-muted)]">—</td>;
                      const val = dirty[cell.id] !== undefined ? dirty[cell.id] : fmtPrice(cell.price);
                      return (
                        <td key={ti.id} className="px-2 py-1">
                          <input
                            type="text"
                            value={val}
                            onChange={e => handleChange(cell.id, e.target.value)}
                            className="w-full text-center text-xs px-1.5 py-1 rounded border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-[var(--text-tertiary)]">{t('calcConfig.noPortoData')}</div>
      )}

      {showInit && (
        <PortoInitModal
          t={t}
          carriers={carriers}
          warehouses={warehouses}
          countries={countries}
          onClose={() => setShowInit(false)}
          onSaved={() => { setShowInit(false); load(); }}
        />
      )}
    </div>
  );
}

function PortoInitModal({ t, carriers, warehouses, countries, onClose, onSaved }) {
  const [form, setForm] = useState({
    carrier_id: carriers[0]?.id || '',
    warehouse_code: warehouses[0]?.code || '',
    country_code: countries[0]?.code || '',
    weightInput: '1,3,5,10,15,20,25,31.5',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const weights = form.weightInput.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n) && n > 0).sort((a, b) => a - b);
    const weight_classes = weights.map((w, i) => ({
      weight_min: i === 0 ? 0 : weights[i - 1],
      weight_max: w,
    }));
    await apiPost('/api/preiskalkulator/porto-rates/init', {
      carrier_id: parseInt(form.carrier_id),
      warehouse_code: form.warehouse_code,
      country_code: form.country_code,
      weight_classes,
    });
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('calcConfig.initRoute')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calculator.carrier')}</span>
            <select value={form.carrier_id} onChange={e => setForm(f => ({ ...f, carrier_id: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
              {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calculator.warehouse')}</span>
              <select value={form.warehouse_code} onChange={e => setForm(f => ({ ...f, warehouse_code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
                {warehouses.map(w => <option key={w.code} value={w.code}>{w.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calculator.country')}</span>
              <select value={form.country_code} onChange={e => setForm(f => ({ ...f, country_code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
                {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.weightClasses')} (kg, {t('calcConfig.commaSeparated')})</span>
            <input value={form.weightInput} onChange={e => setForm(f => ({ ...f, weightInput: e.target.value }))}
              placeholder="1, 3, 5, 10, 15, 20, 25, 31.5"
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : t('calcConfig.createRates')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  4) EXTRAS TAB — CRUD with per-tier pricing
// ═══════════════════════════════════════════════════════════════════════
function ExtrasTab({ t }) {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, ti] = await Promise.all([
      apiFetch('/api/preiskalkulator/extra-services'),
      apiFetch('/api/preiskalkulator/volume-tiers'),
    ]);
    setItems(s);
    setTiers(ti);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcConfig.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/extra-services/${id}`);
    load();
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcConfig.extrasDesc')}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={13} /> {t('calcConfig.addExtra')}
        </button>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colName')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colCode')}</th>
              {tiers.map(ti => (
                <th key={ti.id} className="text-right px-3 py-2 text-[var(--text-secondary)] font-medium">{ti.name}</th>
              ))}
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colActive')}</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                <td className="px-3 py-2 text-[var(--text-primary)] font-medium">
                  <div className="flex items-center gap-2">
                    {it.color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: it.color }} />}
                    {it.name}
                  </div>
                </td>
                <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{it.code}</td>
                {tiers.map(ti => {
                  const p = it.pricing?.find(pr => pr.volume_tier_id === ti.id);
                  return (
                    <td key={ti.id} className="px-3 py-2 text-right text-[var(--text-primary)]">
                      {p ? `${fmtPrice(p.price)} €` : '—'}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center">
                  {it.active ? <Check size={14} className="inline text-green-500" /> : <X size={14} className="inline text-[var(--text-muted)]" />}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Pencil size={13} className="text-[var(--text-secondary)]" /></button>
                    <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Trash2 size={13} className="text-[var(--status-error)]" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3 + tiers.length} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('calcConfig.noData')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <ExtrasModal
          t={t}
          item={modal === 'new' ? null : modal}
          tiers={tiers}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function ExtrasModal({ t, item, tiers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    code: item?.code || '',
    description: item?.description || '',
    color: item?.color || '#338edc',
    sort_order: item?.sort_order ?? 0,
    active: item?.active ?? true,
  });
  const [pricing, setPricing] = useState(() =>
    tiers.map(ti => {
      const existing = item?.pricing?.find(p => p.volume_tier_id === ti.id);
      return { volume_tier_id: ti.id, name: ti.name, price: existing ? fmtPrice(existing.price) : '0,00' };
    })
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      sort_order: parseInt(form.sort_order) || 0,
      pricing: pricing.map(p => ({ volume_tier_id: p.volume_tier_id, price: parseLocaleNum(p.price) })),
    };
    if (item) {
      await apiPut(`/api/preiskalkulator/extra-services/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/extra-services', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-lg p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcConfig.editExtra') : t('calcConfig.addExtra')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colName')} *</span>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colCode')} *</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colDescription')}</span>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colColor')}</span>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full h-8 rounded-lg border border-[var(--border-input)] cursor-pointer" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.sortOrder')}</span>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
              <span className="text-xs text-[var(--text-secondary)]">{t('calcConfig.colActive')}</span>
            </label>
          </div>

          {/* Per-tier pricing */}
          <div>
            <span className="text-xs font-medium text-[var(--text-primary)] block mb-2">{t('calcConfig.tierPricing')}</span>
            <div className="grid grid-cols-2 gap-2">
              {pricing.map((p, i) => (
                <label key={p.volume_tier_id} className="flex items-center gap-2">
                  <span className="text-xs text-[var(--text-secondary)] w-20 truncate">{p.name}</span>
                  <input
                    value={p.price}
                    onChange={e => {
                      const next = [...pricing];
                      next[i] = { ...next[i], price: e.target.value };
                      setPricing(next);
                    }}
                    className="flex-1 px-2 py-1 text-xs text-right rounded border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none"
                  />
                  <span className="text-xs text-[var(--text-tertiary)]">€</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  5) TIERS TAB — CRUD table
// ═══════════════════════════════════════════════════════════════════════
function TiersTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await apiFetch('/api/preiskalkulator/volume-tiers'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcConfig.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/volume-tiers/${id}`);
    load();
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcConfig.tiersDesc')}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={13} /> {t('calcConfig.addTier')}
        </button>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colName')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colIcon')}</th>
              <th className="text-right px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colMin')}</th>
              <th className="text-right px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colMax')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colColor')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.sortOrder')}</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{it.name}</td>
                <td className="px-3 py-2 text-center">{it.icon || '—'}</td>
                <td className="px-3 py-2 text-right text-[var(--text-primary)]">{it.min_packages?.toLocaleString('de-DE') || '—'}</td>
                <td className="px-3 py-2 text-right text-[var(--text-primary)]">{it.max_packages?.toLocaleString('de-DE') || '—'}</td>
                <td className="px-3 py-2 text-center">
                  {it.color && <span className="inline-block w-4 h-4 rounded-full border border-[var(--border-default,#E5E7EB)]" style={{ background: it.color }} />}
                </td>
                <td className="px-3 py-2 text-center text-[var(--text-secondary)]">{it.sort_order}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Pencil size={13} className="text-[var(--text-secondary)]" /></button>
                    <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Trash2 size={13} className="text-[var(--status-error)]" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('calcConfig.noData')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <TierModal
          t={t}
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function TierModal({ t, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    min_packages: item?.min_packages ?? '',
    max_packages: item?.max_packages ?? '',
    icon: item?.icon || '',
    color: item?.color || '#338edc',
    sort_order: item?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      min_packages: form.min_packages !== '' ? parseInt(form.min_packages) : null,
      max_packages: form.max_packages !== '' ? parseInt(form.max_packages) : null,
      sort_order: parseInt(form.sort_order) || 0,
    };
    if (item) {
      await apiPut(`/api/preiskalkulator/volume-tiers/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/volume-tiers', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcConfig.editTier') : t('calcConfig.addTier')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colName')} *</span>
            <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colMin')} ({t('calcConfig.packages')})</span>
              <input type="number" value={form.min_packages} onChange={e => setForm(f => ({ ...f, min_packages: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colMax')} ({t('calcConfig.packages')})</span>
              <input type="number" value={form.max_packages} onChange={e => setForm(f => ({ ...f, max_packages: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colIcon')}</span>
              <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="e.g. 📦"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colColor')}</span>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full h-8 rounded-lg border border-[var(--border-input)] cursor-pointer" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.sortOrder')}</span>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  6) CATEGORIES TAB — CRUD table
// ═══════════════════════════════════════════════════════════════════════
function CategoriesTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await apiFetch('/api/preiskalkulator/categories'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcConfig.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/categories/${id}`);
    load();
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcConfig.categoriesDesc')}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={13} /> {t('calcConfig.addCategory')}
        </button>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colName')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colCode')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colDescription')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colColor')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcConfig.colActive')}</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                <td className="px-3 py-2 text-[var(--text-primary)] font-medium">
                  <div className="flex items-center gap-2">
                    {it.color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: it.color }} />}
                    {it.name}
                  </div>
                </td>
                <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{it.code}</td>
                <td className="px-3 py-2 text-[var(--text-secondary)] max-w-[200px] truncate">{it.description || '—'}</td>
                <td className="px-3 py-2 text-center">
                  {it.color && <span className="inline-block w-4 h-4 rounded-full border border-[var(--border-default,#E5E7EB)]" style={{ background: it.color }} />}
                </td>
                <td className="px-3 py-2 text-center">
                  {it.active ? <Check size={14} className="inline text-green-500" /> : <X size={14} className="inline text-[var(--text-muted)]" />}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Pencil size={13} className="text-[var(--text-secondary)]" /></button>
                    <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Trash2 size={13} className="text-[var(--status-error)]" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('calcConfig.noData')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <CategoryModal
          t={t}
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function CategoryModal({ t, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    code: item?.code || '',
    description: item?.description || '',
    color: item?.color || '#338edc',
    sort_order: item?.sort_order ?? 0,
    active: item?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, sort_order: parseInt(form.sort_order) || 0 };
    if (item) {
      await apiPut(`/api/preiskalkulator/categories/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/categories', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcConfig.editCategory') : t('calcConfig.addCategory')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colName')} *</span>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colCode')} *</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colDescription')}</span>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.colColor')}</span>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full h-8 rounded-lg border border-[var(--border-input)] cursor-pointer" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcConfig.sortOrder')}</span>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
              <span className="text-xs text-[var(--text-secondary)]">{t('calcConfig.colActive')}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
